import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import AuthLayout from "../../layouts/AuthLayout";
import useAuth from "../../hooks/useAuth";
import "./Login.css";

/**
 * Same split-panel structure as the old app's login.html (branded left
 * panel + right-side form with username/password icons, a show/hide
 * password toggle, "Remember Me" and "Forgot Password?") — recolored to
 * the exact navy (#000080) / white brand instead of the old app's blue
 * gradient. The old page's jQuery bubble background animation is not
 * ported (this app doesn't carry the old jQuery stack); the decorative
 * glow circles are recreated in CSS only.
 */
export default function Login() {
  const { login } = useAuth();
  const history = useHistory();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(username, password);
      // Matches digicampusbd's own PageController#homePage — /home is the
      // real post-login redirect target there; /dashboard is a separate
      // page reached only via the sidebar's own "dashboard" link.
      history.push("/home");
      // Don't touch state after this — history.push unmounts Login on the
      // next render, and setSubmitting(false) here would warn about setting
      // state on an unmounted component.
    } catch (err) {
      const message =
        (err.response && err.response.data && err.response.data.message) ||
        "Login failed. Please try again.";
      setError(message);
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout>
      <div className="login-left">
        <span className="login-glow-circle" />
        <img src="/images/digi-main_log.png" alt="Digicampus" className="login-logo" />
      </div>
      <div className="login-right">
        <h2 className="login-title">Login</h2>
        <form onSubmit={handleSubmit} className="login-form">
          <div className="login-input-group">
            <input
              type="text"
              placeholder="Username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              required
            />
            <span className="login-input-icon" aria-hidden="true">
              👤
            </span>
          </div>
          <div className="login-input-group">
            <input
              type={showPassword ? "text" : "password"}
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
            />
            <span
              className="login-input-icon login-toggle-password"
              role="button"
              tabIndex={0}
              aria-label={showPassword ? "Hide password" : "Show password"}
              onClick={() => setShowPassword((v) => !v)}
              onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && setShowPassword((v) => !v)}
            >
              {showPassword ? "🙈" : "👁️"}
            </span>
          </div>
          <div className="login-input-group login-remember-me">
            <input
              type="checkbox"
              id="remember"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
            />
            <label htmlFor="remember" className="login-checkbox-label">
              Remember Me
            </label>
          </div>

          {error && <div className="login-error">{error}</div>}

          <button type="submit" className="login-btn" disabled={submitting}>
            {submitting ? "Signing in..." : "Log In"}
          </button>
        </form>
        <div className="login-forgot-password">
          <a href="/forgot-password">Forgot Password?</a>
        </div>
      </div>
    </AuthLayout>
  );
}
